
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Upload, Calendar as CalendarIcon, Clock } from "lucide-react";
import { useState } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface AddMissionDialogProps {
  onSubmit: (title: string, description: string, category: string, attachmentUrl?: string, missionType?: string, startsAt?: string, expiresAt?: string) => Promise<void>;
  disabled?: boolean;
}

export const AddMissionDialog = ({ onSubmit, disabled }: AddMissionDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newMission, setNewMission] = useState({
    title: "",
    description: "",
    category: "",
    missionType: "continued" as "limited" | "continued",
  });
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [startTime, setStartTime] = useState("09:00");
  const [expirationDate, setExpirationDate] = useState<Date | undefined>();
  const [expirationTime, setExpirationTime] = useState("17:00");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    setIsUploading(true);
    try {
      let attachmentUrl = undefined;
      
      if (file) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('mission-attachments')
          .upload(fileName, file);

        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('mission-attachments')
          .getPublicUrl(fileName);

        attachmentUrl = publicUrl;
      }

      // Prepare start and expiration datetime if mission is limited
      let startsAt: string | undefined;
      let expiresAt: string | undefined;
      
      if (newMission.missionType === "limited") {
        if (startDate) {
          const [startHours, startMinutes] = startTime.split(':');
          const startDateTime = new Date(startDate);
          startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
          startsAt = startDateTime.toISOString();
        }
        
        if (expirationDate) {
          const [endHours, endMinutes] = expirationTime.split(':');
          const expirationDateTime = new Date(expirationDate);
          expirationDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 59, 999);
          expiresAt = expirationDateTime.toISOString();
        }
      }

      await onSubmit(
        newMission.title, 
        newMission.description, 
        newMission.category, 
        attachmentUrl,
        newMission.missionType,
        startsAt,
        expiresAt
      );
      
      setNewMission({ title: "", description: "", category: "", missionType: "continued" });
      setFile(null);
      setStartDate(undefined);
      setStartTime("09:00");
      setExpirationDate(undefined);
      setExpirationTime("17:00");
      setIsOpen(false);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button disabled={disabled} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add New Mission
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Mission</DialogTitle>
          <DialogDescription>
            Create a new mission by providing details below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={newMission.title}
              onChange={(e) =>
                setNewMission({ ...newMission, title: e.target.value })
              }
              placeholder="Enter mission title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              value={newMission.category}
              onChange={(e) =>
                setNewMission({ ...newMission, category: e.target.value })
              }
              placeholder="Enter mission category"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={newMission.description}
              onChange={(e) =>
                setNewMission({ ...newMission, description: e.target.value })
              }
              placeholder="Enter mission description"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="missionType">Mission Type</Label>
            <Select
              value={newMission.missionType}
              onValueChange={(value: "limited" | "continued") =>
                setNewMission({ ...newMission, missionType: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select mission type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="continued">Continued</SelectItem>
                <SelectItem value="limited">Limited Duration</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {newMission.missionType === "limited" && (
            <>
              <div className="space-y-2">
                <Label>Valid From Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, "PPP") : "Pick start date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[560px] max-w-[90vw] p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="startTime">Valid From Time</Label>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <Input
                    id="startTime"
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Valid Until Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !expirationDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {expirationDate ? format(expirationDate, "PPP") : "Pick end date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[560px] max-w-[90vw] p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={expirationDate}
                      onSelect={setExpirationDate}
                      disabled={(date) => date < (startDate || new Date())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="expirationTime">Valid Until Time</Label>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <Input
                    id="expirationTime"
                    type="time"
                    value={expirationTime}
                    onChange={(e) => setExpirationTime(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="attachment">Attachment</Label>
            <div className="flex items-center gap-2">
              <Input
                id="attachment"
                type="file"
                onChange={handleFileChange}
                className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              {file && <p className="text-sm text-muted-foreground">{file.name}</p>}
            </div>
          </div>
          <Button 
            onClick={handleSubmit} 
            className="w-full"
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Upload className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              'Create Mission'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
